import { ReactElement, useEffect, useState } from "react";
import Head from "next/head";
import { MainLayout } from "@/containers/layout/KoachLayout";
import isMobile from "ismobilejs";
import getConfig from "next/config";
import { mobileConversionDownloadButton } from "@/components/shared/gtm";
import QRCodeModal from "@/components/koach/Shared/QRCodeModal";
import SignupButtonComponent from "@/components/koach/Shared/SignupButtonComponent";
import { useRouter } from "next/router";

const DownloadApp = () => {
  const { publicRuntimeConfig } = getConfig();
  const [showModal, setShowModal] = useState(false);
  const { asPath } = useRouter();

  const handleModalClose = () => {
    setShowModal(false);
    window.location.href = publicRuntimeConfig.NEXT_PUBLIC_URL;
  };

  useEffect(() => {
    const mobileDevice = isMobile(window.navigator);
    let branchLink = publicRuntimeConfig.NEXT_PUBLIC_BRANCH_DOWNLOAD_URL;
    const queryParams = asPath.split("?")[1];

    mobileConversionDownloadButton();

    if (mobileDevice.any) {
      if (queryParams) {
        branchLink += `?${queryParams}`;
      }
      window.open(branchLink, "_self");
    } else {
      setShowModal(true);
    }
  }, [asPath]);

  return (
    <>
      <Head>
        <title>Download the Klink Mobile App for Free</title>
        <link rel="canonical" href="https://www.klink-app.com/how-it-works" />
        <meta
          name="description"
          content="Klink App for people who want to get a mortgage and buy a home in the UK. Download the app for free and start saving for your deposit."
        />

        <meta
          name="twitter:card"
          content="Klink App for people who want to get a mortgage and buy a home in the UK. Download the app for free and start saving for your deposit."
          key="twcard"
        />
        <meta name="twitter:url" content="https://www.klink-app.com/download" />
        <meta
          name="twitter:title"
          content="Download the Klink Mobile App for Free"
        />
        <meta name="twitter:image" content="/meta-opengraph-download.png" />
        {/* TODO */}
        <meta name="twitter:creator" content="klink_app" key="twhandle" />

        <meta
          property="og:url"
          content="https://www.klink-app.com/download"
          key="ogurl"
        />
        <meta property="og:url" content="/" key="ogurl" />
        <meta
          property="og:image"
          content="/meta-opengraph-download.png"
          key="ogimage"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Klink" key="ogsitename" />
        <meta
          property="og:title"
          content="Download the Klink Mobile App for Free"
          key="ogtitle"
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Klink App for people who want to get a mortgage and buy a home in the UK. Download the app for free and start saving for your deposit."
          key="ogdesc"
        />
      </Head>

      <div className="md:py-10 grid grid-cols-1 gap-1 md:gap-0 bg-navy w-full overflow-hidden text-white md:px-6 px-5 lg:px-11 xl:px-32">
        <h1 className="text-4xl lg:text-5xl 2xl:text-6xl font-bold text-center md:text-left text-landing-white leading-[1.1]">
          Download the Klink App Today!
        </h1>
        <br />
        <SignupButtonComponent />

        <br />
        <br />
        <h2 className="text-2xl lg:text-3xl 2xl:text-4xl font-bold text-center md:text-left text-landing-white leading-[1.1]">
          Your Path to Home Ownership
        </h2>

        <p className="block px-1 text-xl text-grey-200">
          The Klink app is designed to empower future home buyers by providing
          essential tools for saving, budgeting, and achieving financial goals.
          With Klink, you can set specific home buying targets, track your
          savings progress, and gain valuable insights into your spending
          habits. This all-in-one app simplifies the journey to home ownership,
          making it easier than ever to get on the property ladder.
        </p>
        <br />
        <p className="block px-1 text-xl text-grey-200">
          By using Klink, you can create a personalised plan that aligns with
          your financial situation and aspirations. The app not only helps you
          visualise your goals but also offers tailored missions to enhance your
          savings. Whether you're just starting to think about buying a home or
          are already on your way, Klink provides the support you need to
          succeed.
        </p>

        <br />
        <br />
        <h2 className="text-2xl lg:text-3xl 2xl:text-4xl font-bold text-center md:text-left text-landing-white leading-[1.1]">
          Features That Make Klink Stand Out
        </h2>

        <p className="block px-1 text-xl text-grey-200">
          Klink offers a unique combination of features that set it apart from
          other financial apps. With its goal-setting capabilities, you can
          determine how much you need to save for your deposit and how long it
          will take to reach that target. The app also provides detailed
          spending insights, helping you identify areas where you can cut back
          and save more effectively.
        </p>
        <br />
        <p className="block px-1 text-xl text-grey-200">
          Additionally, Klink rewards you for your financial discipline with
          Klink coins, which can be converted into cash towards your home
          deposit. This gamified approach not only makes saving more engaging
          but also motivates you to stay on track. Start your journey towards
          home ownership today by downloading the Klink app and take the first
          step towards realising your dream of owning a home.
        </p>
        <br />
        <br />
      </div>

      <QRCodeModal showModal={showModal} onClick={handleModalClose} />
    </>
  );
};

DownloadApp.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout>{page}</MainLayout>;
};

export default DownloadApp;
